'use client';

import { usePathname } from 'next/navigation';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import AuthLayout from '~/shared/components/auth-layout';
import Button from '~/shared/components/form-fields/button';
import WebLayout from '~/shared/components/web-layout';
import { LOCALES, type Locale, PAGE_NAMES } from '~/shared/constants';
import { getLocaleLink } from '~/shared/helpers/browser-helpers';

import '~/shared/styles/public-status-pages.scss';

type GlobalErrorBoundaryProps = {
  children: ReactNode;
  lng?: string;
  user?: unknown;
};

type InnerBoundaryProps = GlobalErrorBoundaryProps & {
  resetKey: string;
};

type BoundaryState = {
  hasError: boolean;
  error?: Error | null;
};

const isNextNotFoundError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }

  const digest = (error as Error & { digest?: string }).digest ?? '';

  return error.message === 'NEXT_HTTP_ERROR_FALLBACK;404' || digest === 'NEXT_HTTP_ERROR_FALLBACK;404';
};

const getSafeLocale = (lng?: string): Locale => {
  if (lng === LOCALES.UA || lng === LOCALES.RU || lng === LOCALES.EN) {
    return lng;
  }

  return LOCALES.UA;
};

class GlobalErrorBoundaryInner extends Component<InnerBoundaryProps, BoundaryState> {
  constructor(props: InnerBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidUpdate(prevProps: InnerBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({
        hasError: false,
        error: null,
      });
    }
  }

  componentDidCatch(error: Error & { digest?: string }, errorInfo: ErrorInfo) {
    if (isNextNotFoundError(error)) {
      return;
    }

    console.group('‚ùå Global React Error');
    console.error('message:', error?.message);
    console.error('digest:', error?.digest);
    console.error('stack:', error?.stack);
    console.error('componentStack:', errorInfo?.componentStack);
    console.error('url:', typeof window !== 'undefined' ? window.location.href : 'n/a');
    console.error('userAgent:', typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a');
    console.groupEnd();

    if (typeof window !== 'undefined') {
      (window as Window & { __LAST_REACT_ERROR__?: unknown }).__LAST_REACT_ERROR__ = {
        message: error?.message,
        digest: error?.digest,
        stack: error?.stack,
        componentStack: errorInfo?.componentStack,
        url: window.location.href,
      };
    }
  }

  private handleGoHome = () => {
    const lng = getSafeLocale(this.props.lng);
    const href = getLocaleLink(PAGE_NAMES.HOME, lng);

    window.location.assign(href);
  };

  private renderNotFound() {
    const lng = getSafeLocale(this.props.lng);

    return (
      <WebLayout isNoFooter lng={lng} user={this.props.user}>
        <AuthLayout
          isRedirection={false}
          actions={<></>}
          Content={({ t }) => (
            <div className="public-status-page">
              <span className="public-status-page__eyebrow">Error</span>
              <h1 className="public-status-page__title">{t('page.not-found.title')}</h1>
              <div className="public-status-page__code">404</div>
              <p className="public-status-page__description">
                {t('text.the-request-page')} {t('text.does-not-exist')}
              </p>
              <div className="public-status-page__actions">
                <Button size="large" type="primary" className="call-to-action-button--purple" onClick={this.handleGoHome}>
                  {t('button.go-home')}
                </Button>
              </div>
            </div>
          )}
        />
      </WebLayout>
    );
  }

  private renderSystemError() {
    const lng = getSafeLocale(this.props.lng);
    const digest = (this.state.error as (Error & { digest?: string }) | null)?.digest;

    return (
      <WebLayout isNoFooter lng={lng} user={this.props.user}>
        <AuthLayout
          isRedirection={false}
          actions={<></>}
          Content={({ t }) => (
            <div className="public-status-page">
              <span className="public-status-page__eyebrow">Error</span>
              <h1 className="public-status-page__title">{t('text.system-error')}</h1>
              <div className="public-status-page__code">!</div>
              <p className="public-status-page__description">{t('text.something-went-wrong')}</p>

              {process.env.NEXT_PUBLIC_DEBUG_PROD_ERRORS === 'true' && (
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '100%', textAlign: 'left' }}>
                  {JSON.stringify(
                    {
                      message: this.state.error?.message,
                      digest,
                      stack: this.state.error?.stack,
                    },
                    null,
                    2,
                  )}
                </pre>
              )}

              <div className="public-status-page__actions">
                <Button size="large" type="primary" className="call-to-action-button--purple" onClick={this.handleGoHome}>
                  {t('button.go-home')}
                </Button>
              </div>
            </div>
          )}
        />
      </WebLayout>
    );
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (isNextNotFoundError(this.state.error)) {
      return this.renderNotFound();
    }

    return this.renderSystemError();
  }
}

const GlobalErrorBoundary = ({ children, lng, user }: GlobalErrorBoundaryProps) => {
  const pathname = usePathname();

  return (
    <GlobalErrorBoundaryInner lng={lng} user={user} resetKey={pathname ?? ''}>
      {children}
    </GlobalErrorBoundaryInner>
  );
};

export default GlobalErrorBoundary;
